import React, { useState, useEffect } from 'react'
import { InputGroup, H5, Classes } from '@blueprintjs/core'
import {
  IconButton,
  Confirmation,
  Flex,
  Button,
  Toaster,
  SpaceCard,
  SpaceCardSkeleton,
  SpaceSelect,
  Spinner,
  IGridTableCell,
  NewGridTable,
  GridTableStatusEnum,
  IGridTableColumn,
} from '@ui'
import api, { IWorkplaceGroupSpace } from '@api'
import { UseWorkplaceGroupHook } from './hooks/useWorkplaceGroup'
import { pluralize } from '@utils'
import { useReload } from '@hooks/reloadContext'
import { useSelection } from '@hooks'
import useWorkplaceGroupSpaces from './hooks/useWorkplaceGroupSpaces'
import track from '@track'
import Event from '@track/Event'
import css from './spacesManageSection.module.scss'

const placeholder = (
  <span data-cy="placeholder" className={Classes.SKELETON}>
    placeholder
  </span>
)

export default function SpacesManageSection({
  workplaceGroup,
}: {
  workplaceGroup: UseWorkplaceGroupHook
}) {
  const selection = useSelection()
  const reload = useReload()
  const [disableConfirmation, setDisableConfirmation] = useState(false)

  const [showSearch, setShowSearch] = useState(false)
  const [withUndo, setWithUndo] = useState<IWorkplaceGroupSpace[]>([])

  const spaces = useWorkplaceGroupSpaces(workplaceGroup.id)

  useEffect(() => {
    setWithUndo(spaces.list)
  }, [spaces.list])

  const helperText = selection.count
    ? `${selection.count} selected`
    : spaces.query
    ? `${spaces.count} filtered ${pluralize(spaces.count, 'record')}`
    : `${spaces.count} ${pluralize(spaces.count, 'record')}`

  const columnsSpaces: IGridTableColumn<IWorkplaceGroupSpace>[] = [
    {
      label: 'Space',
      Cell: ({ cell }) => (cell ? <SpaceCard space={cell.data} /> : <SpaceCardSkeleton />),
    },
    {
      label: 'Department Allocation',
      Cell: ({ cell, skeleton }) => {
        if (skeleton) {
          return (
            <div className="text-sm">
              <span className={Classes.SKELETON}>Department name</span>
            </div>
          )
        }

        const space = (cell as IGridTableCell<IWorkplaceGroupSpace>).data

        return (
          <div>
            {space.spaceAssignment?.department
              ? `${space.spaceAssignment.department.name}${
                  space.spaceAssignment?.team ? ' | ' + space.spaceAssignment.team.name : ''
                }`
              : '-'}
          </div>
        )
      },
    },
    {
      label: '',
      Cell: ({ cell, isHighlighted }) => {
        if (!cell) return null

        const space = (cell as IGridTableCell<IWorkplaceGroupSpace>).data

        return (
          <div
            data-button={`remove button ${space.externalId}`}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}>
            {isHighlighted && (
              <IconButton
                round
                minimal
                className="remove-icon"
                iconName="times"
                data-button={`remove ${space.externalId}`}
                tooltip={`Remove ${space.externalId}`}
                style={{ border: 'none' }}
                onClick={() => {
                  const removedSpace = space

                  setWithUndo((prev) => [...prev.filter((i) => space.id !== i.id)])
                  selection.clear()
                  api.workplaceGroup
                    .removeSpaces([space.id], workplaceGroup.id)
                    .then(() => reload.workplaceGroups())

                  track.workplaceGroupDialog({
                    eventName: Event.RECORD_REMOVE,
                    props: {
                      component: 'Space Manage',
                    },
                  })

                  Toaster.success({
                    action: {
                      onClick: () => {
                        api.workplaceGroup
                          .addSpaces(workplaceGroup.id, [space.id])
                          .then(() => reload.workplaceGroups())
                        setWithUndo((prev) => [removedSpace, ...prev])

                        track.workplaceGroupDialog({
                          eventName: Event.UNDO_CLICK,
                          props: {
                            component: 'Space Manage',
                            actionName: 'remove',
                          },
                        })
                      },
                      text: 'Undo',
                    },
                    message: 'Space removed from workplace group',
                  })
                }}
              />
            )}
          </div>
        )
      },
    },
  ]

  const formatItems = (items: IWorkplaceGroupSpace[]) => {
    return items.map((i) => ({
      data: i,
    }))
  }

  const formatedItems = formatItems(withUndo)

  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        style={{
          marginTop: 40,
          height: 40,
        }}>
        <Flex align="center">
          <H5 style={{ marginBottom: 0, marginRight: 6, lineHeight: '30px' }}>Spaces</H5>
          {showSearch ? (
            <InputGroup
              autoFocus
              leftIcon="search"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                spaces.run(e.target.value)
              }}
              placeholder="Search spaces..."
              rightElement={
                <RightElement
                  showWithoutQuery
                  query={spaces.query}
                  onClear={() => {
                    if (spaces.query) spaces.run('')
                    setShowSearch(false)
                  }}
                  isQueryResultLoading={spaces.isLoading}
                />
              }
              value={spaces.query}
            />
          ) : (
            <IconButton
              round
              minimal
              data-button="search_spaces"
              iconName="search"
              onClick={() => setShowSearch(true)}
            />
          )}
        </Flex>
      </Flex>
      <Flex justify="space-between">
        <Flex align="center">
          {Boolean(selection.count) && (
            <span style={{ paddingRight: 10 }}>
              <Confirmation
                shouldHide={disableConfirmation}
                enabled={!disableConfirmation}
                text={`Remove selected ${pluralize(
                  selection.count,
                  'space'
                )} from workplace group?`}
                onConfirm={() => {
                  const spacesForDelete = withUndo.filter((i) =>
                    Object.keys(selection.items).includes(i.id)
                  )

                  const spacesIds = Object.entries(selection.items)
                    .filter(([_, value]) => Boolean(value))
                    .map(([id]) => id)

                  setWithUndo((prev) => [...prev.filter((i) => !spacesIds.includes(i.id))])
                  selection.clear()

                  api.workplaceGroup
                    .removeSpaces(spacesIds, workplaceGroup.id)
                    .then(() =>
                      setWithUndo((prev) => [...prev.filter((i) => !spacesIds.includes(i.id))])
                    )
                    .then(() => reload.workplaceGroups())

                  track.workplaceGroupDialog({
                    eventName: Event.BATCH_ACTION,
                    props: {
                      actionName: 'Delete spaces',
                      actionValue: null,
                      batchCount: spacesIds.length,
                    },
                  })

                  Toaster.success({
                    action: {
                      onClick: () => {
                        setWithUndo((prev) => [...spacesForDelete, ...prev])
                        setDisableConfirmation(false)
                        api.workplaceGroup
                          .addSpaces(workplaceGroup.id, spacesIds)
                          .then(() => reload.workplaceGroups())

                        track.workplaceGroupDialog({
                          eventName: Event.UNDO_CLICK,
                          props: {
                            component: 'Space Manage',
                            actionName: 'remove',
                          },
                        })
                      },
                      text: 'Undo',
                    },
                    message: `${pluralize(selection.count, 'Space')} removed from workplace group`,
                  })
                }}>
                <Button text="Remove" />
              </Confirmation>
            </span>
          )}
          <Flex align="baseline">
            {!spaces.isLoading && <span style={{ color: 'var(--grey1)' }}>{helperText}</span>}
            {spaces.isLoading && placeholder}
            {Boolean(selection.count) && (
              <IconButton
                minimal
                tooltip="Clear selection"
                iconName="times"
                style={{ marginLeft: 4 }}
                data-button="clear_selection"
                onClick={() => {
                  selection.clear()

                  track.workplaceGroupDialog({
                    eventName: Event.CLEAR_SELECTION,
                    props: {
                      component: 'Space Manage',
                    },
                  })
                }}
              />
            )}
          </Flex>
        </Flex>
        <SpaceSelect
          buttonProps={{ text: 'Space', iconName: 'plus' }}
          value={null}
          onChange={(space) => {
            space &&
              api.workplaceGroup
                .addSpaces(workplaceGroup.id, [space.id])
                .then(() => Toaster.success({ message: 'Space added to workplace group' }))
                .then(() => reload.workplaceGroups())

            track.workplaceGroupDialog({
              eventName: Event.RECORD_ADD,
              props: {
                component: 'Space Manage',
              },
            })
          }}
        />
      </Flex>
      <div className={css.table_wrapper}>
        <NewGridTable<IWorkplaceGroupSpace>
          status={
            spaces.isLoading
              ? GridTableStatusEnum.LOADING
              : spaces.isPaging
              ? GridTableStatusEnum.PAGING
              : GridTableStatusEnum.IDLE
          }
          sticky={false}
          customColumnWidths={['auto', 'auto', '50px']}
          noDataMessage={spaces.query ? undefined : 'No Spaces associated yet'}
          columns={columnsSpaces}
          selection={selection}
          data={formatedItems}
          skeletonSize={3}
        />
      </div>
      {spaces.list && spaces.pageInfo.hasNextPage && (
        <Button
          minimal
          style={{ marginTop: 5 }}
          text={`View ${
            spaces.count - spaces.list.length > 5 ? '5' : spaces.count - spaces.list.length
          } more`}
          onClick={() => {
            spaces.loadMore()

            track.workplaceGroupDialog({
              eventName: Event.PAGE,
              props: {
                component: 'Space Manage',
              },
            })
          }}
        />
      )}
    </>
  )
}

function RightElement({
  showWithoutQuery,
  query,
  onClear,
  isQueryResultLoading,
}: {
  query: string
  onClear: () => void
  isQueryResultLoading: boolean
  showWithoutQuery?: boolean
}) {
  if (isQueryResultLoading && query) return <Spinner size={18} />
  if ((showWithoutQuery !== undefined && showWithoutQuery) || query)
    return (
      <IconButton
        minimal
        iconName="times"
        onClick={() => {
          onClear?.()

          track.workplaceGroupDialog({
            eventName: Event.CLEAR_FILTER,
            props: {
              filterName: 'Query',
              filterValue: null,
              component: 'Space Manage',
            },
          })
        }}
      />
    )
  return null
}
